import { HOME_FREE_PALETTE } from "./homeFree";

/**
 * "Home Free Twinkle" (ENG-2291), look 3 of the Home Free crowd light show
 * room simulation: black screens where pops of color bloom and fade back to
 * black at irregular intervals, so the crowd twinkles. 60% of pops are teal;
 * the rest split evenly over purple, salmon and yellow.
 *
 * Each phone's time is cut into slots, and each slot holds one pop at a random
 * point, so the gaps between a phone's pops vary (from back to back to about
 * 4.5s) while any moment is quick to draw. Nothing is synced between phones,
 * so clock differences don't show.
 */

type Ctx = CanvasRenderingContext2D;

/** How long a pop lasts, and the share of it spent blooming (seconds). */
const POP = 0.7;
const BLOOM = 0.25;
/** Each phone's slot length, one pop per slot, is between these (seconds). */
const MIN_SLOT = 1.8;
const MAX_SLOT = 2.6;

const OTHER_COLORS = [
  HOME_FREE_PALETTE.purple,
  HOME_FREE_PALETTE.salmon,
  HOME_FREE_PALETTE.yellow,
];

/** A fixed pseudo-random number in [0, 1) for a phone and a property. */
const hash = (seed: number, salt: number) => {
  const v = Math.sin((seed * 1000 + salt) * 127.1 + 311.7) * 43758.5453;
  return v - Math.floor(v);
};

/** 60% teal, 40% split evenly over the other three. */
const popColor = (r: number) =>
  r < 0.6
    ? HOME_FREE_PALETTE.teal
    : OTHER_COLORS[Math.min(2, Math.floor(((r - 0.6) / 0.4) * 3))];

/** A phone's slot length and how far into a slot the show starts. */
const slots = (seed: number) => ({
  length: MIN_SLOT + hash(seed, 9) * (MAX_SLOT - MIN_SLOT),
  offset: hash(seed, 5),
});

/** When the pop in `slot` starts; it always ends within the slot. */
const popStart = (seed: number, slot: number) => {
  const { length, offset } = slots(seed);
  return (slot - offset) * length + hash(seed, 11 + slot * 17) * (length - POP);
};

export const renderHomeFreeTwinkle = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  seed: number,
) => {
  const { length, offset } = slots(seed);
  const slot = Math.floor(t / length + offset);
  const u = (t - popStart(seed, slot)) / POP;
  const level =
    u < 0 || u >= 1 ? 0 : u < BLOOM ? u / BLOOM : 1 - (u - BLOOM) / (1 - BLOOM);

  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, w, h);
  if (level <= 0) return;
  // A new color each pop.
  ctx.globalAlpha = level;
  ctx.fillStyle = popColor(hash(seed, 7 + slot * 13));
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = 1;
};

/** The first moment this phone's pop is at its brightest, for stills. */
export const homeFreeTwinklePeak = (seed: number) => {
  const { offset } = slots(seed);
  const first = Math.floor(offset);
  const peak = (slot: number) => popStart(seed, slot) + POP * BLOOM;
  return peak(first) >= 0 ? peak(first) : peak(first + 1);
};
